import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { refreshReviewsIoRatingSnippets } from '@theme/reviews-io-ratings';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    if (this.dataset.cartRecommendationSource === 'true') {
      document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    }

    this.#refreshReviewStars();
  }

  /**
   * Injects and initializes Reviews.io stars for recommendation cards.
   */
  async #refreshReviewStars() {
    await refreshReviewsIoRatingSnippets(this);
    requestAnimationFrame(() => {
      refreshReviewsIoRatingSnippets(this);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Ordered product IDs to use as recommendation sources.
   * @returns {string[]}
   */
  #getSourceProductIds() {
    const ids = [];

    if (this.dataset.productId) {
      ids.push(this.dataset.productId);
    }

    if (this.dataset.cartProductIds) {
      ids.push(...this.dataset.cartProductIds.split(',').filter(Boolean));
    }

    return [...new Set(ids)];
  }

  /**
   * @param {string} html
   * @returns {boolean}
   */
  #responseHasRecommendations(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const indicator = parsed.querySelector('[data-has-recommendations]');
    return indicator?.getAttribute('data-has-recommendations') === 'true';
  }

  /**
   * Clears cached responses for this section instance.
   */
  #clearCache() {
    this.#cachedRecommendations = {};
  }

  /**
   * Resets recommendation state so a new fetch can run.
   */
  #resetRecommendationsState() {
    this.dataset.recommendationsPerformed = 'false';
    this.classList.remove('hidden');
    delete this.dataset.error;
    this.#clearCache();
  }

  /**
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (!(event instanceof CustomEvent) || !('detail' in event)) return;

    const cart = event.detail?.resource;
    const items = cart?.items ?? [];
    const productIds = [
      ...new Set(items.map((/** @type {{ product_id?: number }} */ item) => String(item.product_id)).filter(Boolean)),
    ];

    const nextIds = productIds.join(',');
    const nextPrimaryId = productIds[0] ?? '';

    if (nextIds === (this.dataset.cartProductIds ?? '') && nextPrimaryId === (this.dataset.productId ?? '')) {
      return;
    }

    if (productIds.length === 0) {
      this.dataset.cartProductIds = '';
      this.dataset.productId = '';
      this.dataset.recommendationsPerformed = 'true';
      this.classList.add('hidden');
      this.#clearCache();
      return;
    }

    this.dataset.cartProductIds = nextIds;
    this.dataset.productId = nextPrimaryId;
    this.#resetRecommendationsState();
    this.#loadRecommendations();
  };

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { recommendationsPerformed, sectionId, intent } = this.dataset;
    const productIds = this.#getSourceProductIds();

    if (!sectionId) {
      throw new Error('A section ID is required');
    }

    if (productIds.length === 0) {
      if (!Shopify.designMode) {
        this.dataset.recommendationsPerformed = 'true';
        this.classList.add('hidden');
      }
      return;
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#loadRecommendationsForProductIds(productIds, sectionId, intent);
  }

  /**
   * Tries each product ID until recommendations are returned.
   * @param {string[]} productIds
   * @param {string} sectionId
   * @param {string | undefined} intent
   */
  #loadRecommendationsForProductIds(productIds, sectionId, intent) {
    const tryNext = async (index) => {
      if (index >= productIds.length) {
        if (!Shopify.designMode) {
          this.dataset.recommendationsPerformed = 'true';
          this.classList.add('hidden');
        }
        return;
      }

      const productId = productIds[index];

      try {
        const result = await this.#fetchCachedRecommendations(productId, sectionId, intent);

        if (!result.success) {
          if (!Shopify.designMode && index === productIds.length - 1) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return tryNext(index + 1);
        }

        if (result.data?.trim().length && this.#responseHasRecommendations(result.data)) {
          this.dataset.productId = productId;
          this.dataset.recommendationsPerformed = 'true';
          await morphSection(sectionId, result.data, 'full', { injectStylesheet: true });
          await this.#refreshReviewStars();
          return;
        }

        return tryNext(index + 1);
      } catch (error) {
        if (index === productIds.length - 1) {
          this.#handleError(error instanceof Error ? error : new Error(String(error)));
        } else {
          return tryNext(index + 1);
        }
      }
    };

    tryNext(0);
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
